import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  CLUSTER_REVIEW_STATES,
  DEFAULT_PAGE_LIMIT,
  type ClauseCandidate,
  type ClauseCandidateListRequest,
  type ClauseCandidateListResponse,
  type ClusterReviewState,
} from '@clm/shared-types';

import { fetchClauseCandidates } from '../api/clause-candidates';
import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { CursorPager } from '../components/CursorPager';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { ErrorNotice } from '../components/ErrorNotice';
import {
  EMPTY_CURSOR_STACK,
  pageNumber,
  popCursor,
  pushCursor,
  type CursorStack,
} from '../components/cursor-stack';
import { requireSurface } from '../routes/surfaces';
import { CandidatePanel } from './CandidatePanel';
import { ParagraphMatches } from './match-review/ParagraphMatches';

/**
 * Match review — two Legal queues on one surface (CLM-53, CLM-60; FR-16, FR-17,
 * FR-28, NFR-13, CR-01).
 *
 * ## Two modes, two endpoints
 *
 * **Clause candidates** (default) binds `GET /v1/clause-candidates` — the
 * clusters the operator CLI mined from the corpus (CLM-36), waiting for a
 * person to accept, reject, merge or split; the decision goes to
 * `POST /v1/clause-candidates/{cluster_id}/decision`. **Paragraph matches**
 * binds `GET /v1/clause-matches` — the FR-28 per-paragraph MEDIUM and LOW
 * queues the matcher could not settle (CLM-34), resolved through
 * `manage-clause resolve_clause_match` (`ParagraphMatches.tsx`). The toggle is
 * a mode inside the surface, not a route (D-60-1): one nav entry, and only the
 * mounted mode fetches.
 *
 * ## Every filter is a request parameter
 *
 * No client-side sort or filter over a page. The list is corpus-level and
 * unscoped (FR-17), but the same rule holds as on the verification queue: the
 * `totals` are counted server-side and a browser filtering the twenty rows it
 * holds would report a set that matches neither the sample nor the corpus.
 * Every filter change resets the cursor stack — cursors are minted per filter
 * set and a cursor from another set is a 400.
 *
 * ## Prod, today
 *
 * The API has never been deployed (B-33), so in prod the first call fails at
 * the network and this page shows `unknown`. The header, the run line, the
 * filters and a Retry button render **outside** the problem branch so the page
 * is never blank and never a false "empty queue".
 */

interface QueueState {
  readonly response: ClauseCandidateListResponse | null;
  readonly busy: boolean;
  readonly problem: {
    readonly kind: ApiProblemKind;
    readonly detail: string | null;
    readonly correlation_id: string | null;
  } | null;
}

const INITIAL: QueueState = { response: null, busy: true, problem: null };

/** The contract's upper bound on `min_member_count` (ClauseCandidateListRequestSchema). */
const MAX_MIN_MEMBER_COUNT = 1_000;

/** The Queue select: pending is the server default (sends nothing); "all" sends every state. */
type QueueChoice = ClusterReviewState | 'all';

function reviewStateFilter(choice: QueueChoice): readonly ClusterReviewState[] | undefined {
  if (choice === 'pending') return undefined;
  if (choice === 'all') return CLUSTER_REVIEW_STATES;
  return [choice];
}

function similarity(value: number | null): string {
  return value === null ? 'not measured' : value.toFixed(2);
}

export function MatchReview() {
  const surface = requireSurface('review');

  /* CLM-112 — a `?contract_key=` link (the deviation report's "not compared") opens on Paragraph matches, which reads it. */
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<'candidates' | 'paragraphs'>(
    (searchParams.get('contract_key') ?? '').trim() === '' ? 'candidates' : 'paragraphs',
  );
  const [queue, setQueue] = useState<QueueChoice>('pending');
  const [describedOnly, setDescribedOnly] = useState(false);
  const [minMembers, setMinMembers] = useState('');
  /**
   * CLM-105: the queue defaults to the newest mining run so old, superseded
   * runs (§ module header of clause-mining-queries.ts — "no DELETE, ever")
   * don't stay unioned into what a reviewer sees forever. `knownLatestRunId`
   * is learned from the server's own `latest_run`, never guessed locally: the
   * first request of a session is necessarily unfiltered (there is no
   * "give me just the id" endpoint), and the response that comes back is what
   * tells this effect which run to filter to from then on.
   */
  const [runScope, setRunScope] = useState<'current' | 'all'>('current');
  const [knownLatestRunId, setKnownLatestRunId] = useState<string | null>(null);
  const [stack, setStack] = useState<CursorStack>(EMPTY_CURSOR_STACK);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [state, setState] = useState<QueueState>(INITIAL);
  const [openId, setOpenId] = useState<string | null>(null);
  /** Bumped to re-fetch the same page — after a decision, a conflict Reload, or Retry. */
  const [reloadNonce, setReloadNonce] = useState(0);

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // The candidate queue fetches only while its mode is mounted; a switch to
    // Paragraph matches fires no candidate request.
    if (mode !== 'candidates') return;

    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    const reviewState = reviewStateFilter(queue);
    // Clamped to the contract's 1..1000 before the wrapper parses the request:
    // the input's `max` does not stop typing, and a request-side ZodError would
    // otherwise be classified as `off_contract` and blame the API.
    const minMemberCount = Math.min(Number.parseInt(minMembers, 10), MAX_MIN_MEMBER_COUNT);
    // Unfiltered until a run id is known (first load has none to send yet);
    // "all runs" always omits it, restoring today's unioned-across-runs view.
    const miningRunId = runScope === 'current' ? knownLatestRunId : null;
    const request: ClauseCandidateListRequest = {
      ...(reviewState === undefined ? {} : { review_state: [...reviewState] }),
      ...(miningRunId === null ? {} : { mining_run_id: miningRunId }),
      ...(describedOnly ? { described_only: true } : {}),
      ...(Number.isFinite(minMemberCount) && minMemberCount >= 1
        ? { min_member_count: minMemberCount }
        : {}),
      page:
        cursor === undefined ? { limit: DEFAULT_PAGE_LIMIT } : { limit: DEFAULT_PAGE_LIMIT, cursor },
    };

    setState((previous) => ({ ...previous, busy: true }));

    void (async () => {
      try {
        const response = await fetchClauseCandidates(request, controller.signal);
        if (controller.signal.aborted) return;
        setState({ response, busy: false, problem: null });
        // Learn the corpus's newest run from the response itself — never
        // guessed locally. A change here (first load, or a new run landing
        // mid-session) is a dependency of this effect, so it drives exactly
        // one automatic re-fetch, now scoped to that run.
        const observedRunId = response.latest_run?.mining_run_id ?? null;
        if (observedRunId !== knownLatestRunId) {
          setKnownLatestRunId(observedRunId);
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        setState({
          response: null,
          busy: false,
          problem: {
            kind: classifyApiError(error),
            detail: error instanceof ApiError ? error.message : null,
            correlation_id: error instanceof ApiError ? error.correlation_id : null,
          },
        });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [mode, queue, describedOnly, minMembers, cursor, reloadNonce, runScope, knownLatestRunId]);

  /** A filter change invalidates every cursor: they are minted per filter set. */
  const resetPaging = useCallback(() => {
    setStack(EMPTY_CURSOR_STACK);
    setCursor(undefined);
    setOpenId(null);
  }, []);

  const closePanel = useCallback(() => {
    const id = openId;
    setOpenId(null);
    if (id === null) return;
    // Focus returns to the row button that opened the panel (CLM-51).
    document.getElementById(`mr-open-${id}`)?.focus();
  }, [openId]);

  const items = state.response?.items ?? [];
  const totals = state.response?.totals ?? null;
  const page = state.response?.page ?? null;
  const latestRun = state.response === null ? undefined : state.response.latest_run;

  const columns: readonly DataTableColumn<ClauseCandidate>[] = [
    {
      id: 'cluster',
      header: 'Cluster',
      cell: (item) => (
        <>
          #{item.cluster_no} <span className="mut">{item.cluster_id}</span>
        </>
      ),
    },
    {
      id: 'title',
      header: 'Proposed title',
      cell: (item) => (
        <>
          {item.description.title ?? <span className="mut">— not described</span>}
          {item.description.description_withheld ? <> <span className="tag out">withheld</span></> : null}
          {item.description.description_state === 'refused_to_human' ? (
            <> <span className="tag bad">refused</span></>
          ) : null}
          {item.description.description_state === 'skipped_singleton' ? (
            <> <span className="tag">singleton</span></>
          ) : null}
        </>
      ),
    },
    {
      id: 'code',
      header: 'Code',
      cell: (item) => (item.description.clause_code === null ? '—' : <code>{item.description.clause_code}</code>),
    },
    { id: 'members', header: 'Paragraphs', cell: (item) => item.member_count },
    { id: 'contracts', header: 'Contracts', cell: (item) => item.distinct_contract_count },
    {
      id: 'tightness',
      header: 'Similarity',
      cell: (item) =>
        item.deviation.mean_similarity === null && item.deviation.min_similarity === null
          ? 'not measured'
          : `${similarity(item.deviation.mean_similarity)} / ${similarity(item.deviation.min_similarity)}`,
    },
    {
      id: 'state',
      header: 'State',
      cell: (item) => (
        <>
          {item.review_state}
          {item.review_state === 'pending' ? null : (
            <>
              <br />
              <span className="mut">
                {item.reviewed_by_upn ?? '—'} · {item.reviewed_utc ?? '—'}
              </span>
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <div className="hd">
        <div>
          <h2>{surface.label}</h2>
          <p>{surface.summary}</p>
        </div>
      </div>

      <div className="mr-modes" role="tablist" aria-label="Match review mode">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'candidates'}
          onClick={() => {
            setMode('candidates');
          }}
        >
          Clause candidates
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'paragraphs'}
          onClick={() => {
            setMode('paragraphs');
          }}
        >
          Paragraph matches
        </button>
      </div>

      <p className="mut">
        Two queues: clause candidates mined from the corpus (CLM-36) and per-paragraph matches the
        matcher could not settle (CLM-34). FR-28 met end to end (CLM-34, CLM-60).
      </p>

      {mode === 'paragraphs' ? (
        <ParagraphMatches />
      ) : (
        <>
          <p className="mut">
            {latestRun === undefined
              ? 'Looking for the latest mining run…'
              : latestRun === null
                ? 'No mining run has been recorded yet.'
                : `Run ${latestRun.mining_run_id} · started ${latestRun.started_utc} · ${latestRun.outcome} · model ${
                    latestRun.embedding_model ?? 'none'
                  } · join threshold ${
                    latestRun.join_threshold === null ? '—' : latestRun.join_threshold.toFixed(2)
                  } · rules ${latestRun.normalise_rules_version}`}
          </p>

          {runScope === 'current' && latestRun !== null && latestRun !== undefined ? (
            <p className="mut">
              Showing the current mining run only. Superseded runs are not deleted and remain
              reviewable by turning off &quot;Current mining run only&quot; below.
            </p>
          ) : null}

          {totals ? (
            <div className="totals">
              <div className="total">
                <span>Pending</span>
                <b>{totals.pending}</b>
              </div>
              <div className="total">
                <span>Described</span>
                <b>{totals.described}</b>
              </div>
              <div
                className="total"
                title="§4C: every member is processing_restricted; no model was called"
              >
                <span>Withheld — restricted</span>
                <b>{totals.withheld_restricted}</b>
              </div>
              <div className="total" data-tone="bad">
                <span>Refused — waiting for a person</span>
                <b>{totals.refused_to_human}</b>
              </div>
              <div className="total">
                <span>Accepted</span>
                <b>{totals.accepted}</b>
              </div>
            </div>
          ) : null}
    
          <div className="card">
            <h3>Clause candidates</h3>
            <p>
              Each row is a cluster of paragraphs the miner judged to be one clause. Accepting one
              publishes a library clause from wording you type; the model proposes a description, never
              the clause. Every filter below is sent to the API; nothing is filtered in this browser.
            </p>
    
            <div className="vfilters">
              <label className="check">
                Queue
                <select
                  aria-label="Queue"
                  value={queue}
                  onChange={(event) => {
                    setQueue(event.target.value as QueueChoice);
                    resetPaging();
                  }}
                >
                  {CLUSTER_REVIEW_STATES.map((reviewState) => (
                    <option key={reviewState} value={reviewState}>
                      {reviewState.charAt(0).toUpperCase() + reviewState.slice(1)}
                    </option>
                  ))}
                  <option value="all">All</option>
                </select>
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  checked={describedOnly}
                  onChange={(event) => {
                    setDescribedOnly(event.target.checked);
                    resetPaging();
                  }}
                />
                Described only
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  checked={runScope === 'current'}
                  onChange={(event) => {
                    setRunScope(event.target.checked ? 'current' : 'all');
                    resetPaging();
                  }}
                />
                Current mining run only
              </label>
              <label className="check">
                At least this many paragraphs
                <input
                  type="number"
                  min={1}
                  max={MAX_MIN_MEMBER_COUNT}
                  aria-label="At least this many paragraphs"
                  value={minMembers}
                  onChange={(event) => {
                    setMinMembers(event.target.value);
                    resetPaging();
                  }}
                />
              </label>
            </div>
    
            {state.problem ? (
              <ErrorNotice
                kind={state.problem.kind}
                detail={state.problem.detail}
                correlation_id={state.problem.correlation_id}
                action={
                  state.problem.kind === 'not_implemented' ? (
                    <p className="mut">
                      The endpoint is <code>GET /v1/clause-candidates</code>; an empty queue here would
                      be a false claim.
                    </p>
                  ) : state.problem.kind === 'forbidden' ? (
                    <p className="mut">
                      Match review is for CEQ-Legal (<code>legal_reviewer</code>) and Admin. Your
                      sign-in was accepted; your role was not.
                    </p>
                  ) : state.problem.kind === 'unknown' ? (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => {
                        setReloadNonce((n) => n + 1);
                      }}
                    >
                      Retry
                    </button>
                  ) : undefined
                }
              />
            ) : (
              <>
                <DataTable
                  caption="Clause candidates"
                  columns={columns}
                  rows={items}
                  rowKey={(item) => item.cluster_id}
                  isCurrent={(item) => item.cluster_id === openId}
                  busy={state.busy}
                  activate={{
                    header: 'Review',
                    label: (item) => `Review cluster ${String(item.cluster_no)}`,
                    buttonId: (item) => `mr-open-${item.cluster_id}`,
                    onActivate: (item) => {
                      setOpenId(item.cluster_id);
                    },
                  }}
                  empty={
                    latestRun === null ? (
                      <p>
                        <strong>Nothing has been mined yet, so there are no candidates.</strong> This is
                        not a scope effect — the queue is corpus-wide.
                      </p>
                    ) : (
                      <p>
                        <strong>No clusters match these filters.</strong>
                      </p>
                    )
                  }
                />
    
                {page ? (
                  <CursorPager
                    next_cursor={page.next_cursor}
                    historyDepth={stack.cursors.length}
                    busy={state.busy}
                    pageNumber={pageNumber(stack)}
                    returnedCount={page.returned_count}
                    totalInScope={null}
                    onNext={() => {
                      const next = page.next_cursor;
                      if (next === null) return;
                      setStack((current) => pushCursor(current, next));
                      setCursor(next);
                      setOpenId(null);
                    }}
                    onPrevious={() => {
                      const popped = popCursor(stack);
                      setStack(popped.stack);
                      setCursor(popped.cursor);
                      setOpenId(null);
                    }}
                  />
                ) : null}
              </>
            )}
          </div>
    
          {openId === null ? null : (
            <CandidatePanel
              cluster_id={openId}
              joinThreshold={latestRun?.join_threshold ?? null}
              latestRunId={latestRun?.mining_run_id ?? null}
              onClose={closePanel}
              onSettled={() => {
                // Re-fetch rather than patch the row: the totals are server-counted
                // and a locally patched row would disagree with them.
                setReloadNonce((n) => n + 1);
              }}
            />
          )}
        </>
      )}
    </>
  );
}
